import { useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../hooks/useTheme';
import { MaterialSymbol } from '../../icons/MaterialSymbol';
import { ActionDockRing } from './ActionDockRing';
import { ActionDockButton } from './ActionDockButton';
import { ActionDockHealthButton } from './ActionDockHealthButton';
import {
  BAR_SHADOW,
  DARK_BAR_SHADOW,
  DOCK_ICON_COLOR,
  ENHANCE_ICON,
  type ActionDockProps,
} from './constants';

// Metro web returns either a string URI or an asset object for image requires.
const ENHANCE_ICON_SRC: string =
  typeof ENHANCE_ICON === 'string'
    ? ENHANCE_ICON
    : (ENHANCE_ICON?.uri ?? ENHANCE_ICON?.default ?? '');

function EnhanceGlyph() {
  return <img src={ENHANCE_ICON_SRC} width={20} height={20} alt="" />;
}

/**
 * The floating action dock: detached 65px setup ring + 65px gradient bar
 * whose Enhance action morphs the bar into an inline prompt composer.
 * Extracted mechanically from the /cv bottom bar's web twin — pure
 * presentational; the caller supplies the ring, items, and enhance behavior.
 *
 * The leading slot holds either the setup `ring` or, when no ring is passed,
 * the labelled CV Health control (`health`). A ring always wins, so docks
 * that pass one render exactly as before.
 */
export function ActionDock({
  placement = 'fixed',
  ring,
  health,
  items,
  enhance,
}: ActionDockProps) {
  const { t } = useTranslation('common');
  const { theme, isDark } = useTheme();
  const [enhanceOpen, setEnhanceOpen] = useState(false);
  const [enhancePrompt, setEnhancePrompt] = useState('');
  const isContentPlacement = placement === 'content';
  const containerStyle: CSSProperties = {
    position: isContentPlacement ? 'absolute' : 'fixed',
    bottom: isContentPlacement ? 24 : 20,
    left: 0,
    right: 0,
    width: '100%',
    zIndex: 50,
    display: 'flex',
    justifyContent: 'center',
    pointerEvents: 'none',
    paddingInline: 16,
    boxSizing: 'border-box',
  };
  const iconColor = isDark ? theme.text : DOCK_ICON_COLOR;
  const barBackground = isDark
    ? 'linear-gradient(to bottom, color-mix(in srgb, var(--menu-bg) 92%, #fff 8%) 0%, var(--menu-bg) 100%)'
    : 'linear-gradient(to bottom, #F3F3F3 0%, #FFFFFF 60.5%)';

  const openEnhanceComposer = () => {
    if (!enhance) return;
    setEnhancePrompt((current) => current.trim() || enhance.defaultPrompt);
    setEnhanceOpen(true);
  };
  const submitEnhancePrompt = () => {
    const editedPrompt = enhancePrompt.trim();
    if (!editedPrompt || !enhance) return;
    setEnhanceOpen(false);
    setEnhancePrompt('');
    enhance.onSubmit(editedPrompt);
  };

  return (
    <>
      <div className="tabbio-phone-action-dock" style={containerStyle}>
        <div
          style={{
            width: enhanceOpen
              ? 'min(720px, calc(100vw - 32px))'
              : 'fit-content',
            maxWidth: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: enhanceOpen ? 'flex-end' : 'center',
            gap: enhanceOpen ? 0 : 14,
            pointerEvents: 'auto',
          }}
        >
          {enhanceOpen && enhance ? (
            <form
              className="action-dock-enhance-composer"
              onSubmit={(event) => {
                event.preventDefault();
                submitEnhancePrompt();
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
                minHeight: 84,
                borderRadius: 28,
                padding: '6px 8px',
                background: isDark ? theme.surfaceElevated : '#FFFFFF',
                boxShadow: isDark ? DARK_BAR_SHADOW : BAR_SHADOW,
                border: `1px solid ${isDark ? theme.borderStrong : '#E9E9E9'}`,
                boxSizing: 'border-box',
                overflow: 'hidden',
                willChange: 'width, opacity',
              }}
            >
              <button
                type="button"
                aria-label={t('actions.cancel')}
                title={t('actions.cancel')}
                onClick={() => setEnhanceOpen(false)}
                className="action-dock-composer-icon"
                style={{ color: iconColor }}
              >
                <MaterialSymbol name="close" size={19} />
              </button>
              <textarea
                value={enhancePrompt}
                onChange={(event) =>
                  setEnhancePrompt(event.currentTarget.value)
                }
                placeholder={enhance.defaultPrompt}
                autoFocus
                rows={3}
                style={{
                  flex: 1,
                  minWidth: 0,
                  minHeight: 64,
                  maxHeight: 112,
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  color: 'var(--text-primary)',
                  fontSize: 15,
                  lineHeight: '20px',
                  fontFamily: 'inherit',
                  resize: 'none',
                }}
              />
              <button
                type="submit"
                aria-label={enhance.submitA11yLabel}
                title={enhance.submitA11yLabel}
                disabled={!enhancePrompt.trim()}
                className="action-dock-composer-send"
                style={{
                  background: 'var(--text-primary)',
                  color: 'var(--bg)',
                  opacity: enhancePrompt.trim() ? 1 : 0.45,
                }}
              >
                <MaterialSymbol name="arrow_upward" size={20} />
              </button>
            </form>
          ) : (
            <>
              {ring ? (
                <ActionDockRing
                  completionPercent={ring.completionPercent}
                  label={ring.label}
                  a11yLabel={ring.a11yLabel}
                  a11yHint={ring.a11yHint}
                  onPress={ring.onPress}
                />
              ) : health ? (
                <ActionDockHealthButton {...health} />
              ) : null}
              <div
                className="action-dock-bar"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  height: 65,
                  borderRadius: 16,
                  padding: '4px 8px',
                  background: barBackground,
                  boxShadow: isDark ? DARK_BAR_SHADOW : BAR_SHADOW,
                  border: `1px solid ${isDark ? theme.borderStrong : '#E9E9E9'}`,
                  maxWidth: '100%',
                  overflowX: 'auto',
                  scrollbarWidth: 'none',
                }}
              >
                {items.map((item) => (
                  <ActionDockButton
                    key={item.id}
                    icon={item.icon}
                    label={item.label}
                    labelColor={iconColor}
                    onPress={item.onPress}
                  />
                ))}
                {enhance ? (
                  <ActionDockButton
                    icon={<EnhanceGlyph />}
                    label={enhance.label}
                    labelColor={iconColor}
                    onPress={openEnhanceComposer}
                  />
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>
      <style>{`
        .action-dock-bar-button:hover {
          background: var(--hover);
        }
        .action-dock-bar-button:active {
          transform: scale(0.97);
        }
        .action-dock-bar-button:focus-visible,
        .action-dock-setup-ring:focus-visible,
        .action-dock-composer-icon:focus-visible,
        .action-dock-composer-send:focus-visible {
          outline: 2px solid var(--tabbio-focus-ring, var(--focus-ring, #5e29ff));
          outline-offset: 2px;
        }
        .action-dock-composer-icon,
        .action-dock-composer-send {
          width: 38px;
          height: 38px;
          border: none;
          border-radius: 999px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          cursor: pointer;
          padding: 0;
          flex-shrink: 0;
        }
        .action-dock-composer-send:disabled {
          cursor: default;
        }
        .action-dock-enhance-composer {
          animation: action-dock-enhance-expand 260ms cubic-bezier(0.19, 1, 0.22, 1) both;
        }
        .action-dock-enhance-composer textarea,
        .action-dock-enhance-composer .action-dock-composer-icon,
        .action-dock-enhance-composer .action-dock-composer-send {
          animation: action-dock-enhance-content 180ms ease-out 70ms both;
        }
        @keyframes action-dock-enhance-expand {
          from {
            width: 58px;
            opacity: 0.82;
          }
          to {
            width: 100%;
            opacity: 1;
          }
        }
        @keyframes action-dock-enhance-content {
          from {
            opacity: 0;
            transform: translateX(8px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @media (max-width: 640px) {
          .action-dock-bar-button {
            min-width: 36px !important;
            padding-inline: 6px !important;
          }
        }
      `}</style>
    </>
  );
}
